import { Company } from "@domain/CRM/company";
import { Grower } from "@domain/CRM/grower";
import { Job } from "@domain/CRM/job";
import { Inflate, inflate } from "pako";
import { StandardAxiosConfig, getBaseDataURL, parseDates } from "../../../common";
import { CreateAPI_Base, DataAPI_Base, GetAPI_Base, UpdateAPI_Base } from "../base";
import { apiClient } from "../../../client";
import { IAirtableAttachment } from "@domain/CRM/Attachment";
/**
 * Jobs create API — transport-only. The `many`/`one` methods are inherited
 * from `CreateAPI_Base` and make the raw HTTP POST. Callers that need the
 * full creation pipeline (associate growers, salespersons, branches, prep
 * payload) should use `submitJobs` / `submitJob` from
 * `domain/job/jobCreationService` instead of calling this class directly.
 */
class CreateAPI_Jobs extends CreateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}
}

class GetAPI_Jobs extends GetAPI_Base<Job> {
	constructor() {
		super("jobs", Job, true);
	}

	public async byCompany(company: Company, branchNames?: string[]): Promise<Job[]> {
		if (!company.name) return [];
		// Compressed (zlib) and lite: several thousand jobs are ~52MB raw JSON,
		// most of it attachment URL arrays. The lite payload omits attachments
		// (job.attachments === null); consumers load them on demand via
		// jobsList.ensureAttachments().
		const url = `${getBaseDataURL("jobs")}/get-by-company?compressed=true&include_attachments=false`;
		const body: { company_name: string; branch_names?: string[] } = { company_name: company.name };
		if (branchNames && branchNames.length > 0) body.branch_names = branchNames;
		const response = await apiClient.post(url, body, StandardAxiosConfig(0, { isCompressed: true }));
		if (response.status !== 200) {
			throw new Error(`Failed to get jobs for company`);
		}
		const decompressed = inflate(await (response.data as Blob).arrayBuffer(), { to: "string" });
		const records = JSON.parse(decompressed) as Job[];
		const jobs = records.map((j) => Object.assign(new Job(), j));
		parseDates<Job>(jobs);
		return jobs;
	}

	/**
	 * Retrieves unready jobs associated with the specified growers.
	 *
	 * @param growers - An array of `Grower` objects to filter unready jobs by. If empty or undefined, returns an empty array.
	 */
	public async byGrowers(growers: Grower[]): Promise<Job[]> {
		if (!growers || growers.length === 0) return [];
		const url = `${getBaseDataURL("jobs")}/get-unready-for-grower`;
		const body = { grower_names: growers.map((g) => g.name) };
		const response = await apiClient.post<Job[]>(url, body, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get unready jobs for growers`);
		}
		const jobs = response.data.map((j) => Object.assign(new Job(), j));
		parseDates<Job>(jobs);
		return jobs;
	}

	public async unscheduled(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/get-unscheduled`;
		const response = await apiClient.post<Job[]>(url, {}, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get unscheduled jobs`);
		}
		const jobs = response.data.map((j) => Object.assign(new Job(), j));
		parseDates<Job>(jobs);
		return jobs;
	}

	/**
	 * Streaming variant of `byCompany`: the server sends zlib-compressed NDJSON,
	 * one line per batch of (attachment-less) jobs, flushed as Airtable pages
	 * arrive. `onBatch` fires per line so callers can render progressively;
	 * resolves with the full list once the stream ends.
	 */
	public async byCompanyStream(
		company: Company,
		branchNames: string[] | undefined,
		onBatch: (jobs: Job[]) => void,
	): Promise<Job[]> {
		if (!company.name) return [];
		const body: { company_name: string; branch_names?: string[] } = { company_name: company.name };
		if (branchNames && branchNames.length > 0) body.branch_names = branchNames;
		return this.streamJobs(`${getBaseDataURL("jobs")}/get-by-company/stream`, body, onBatch);
	}

	/**
	 * Streaming variant of `unscheduled` for the scheduling map: lite jobs plus
	 * the boundary SHP attachment, batched as the server fetches pages.
	 */
	public async unscheduledStream(onBatch: (jobs: Job[]) => void): Promise<Job[]> {
		return this.streamJobs(`${getBaseDataURL("jobs")}/get-unscheduled/stream`, {}, onBatch);
	}

	/**
	 * POST to a jobs streaming endpoint (zlib-compressed NDJSON; one line per
	 * batch) and surface each batch via `onBatch`; resolves with the full list.
	 *
	 * Uses `fetch` (axios can't stream response bodies in the browser), so the
	 * apiClient interceptors don't apply — auth headers come from
	 * StandardAxiosConfig directly.
	 */
	private async streamJobs(url: string, body: unknown, onBatch: (jobs: Job[]) => void): Promise<Job[]> {
		const config = StandardAxiosConfig();
		const response = await fetch(url, {
			method: "POST",
			headers: { ...(config.headers as Record<string, string>), "Content-Type": "application/json" },
			body: JSON.stringify(body),
		});
		if (!response.ok || !response.body) {
			throw new Error(`Failed to stream jobs`);
		}

		const all: Job[] = [];
		let buffer = "";
		const handleText = (text: string) => {
			buffer += text;
			let newline: number;
			while ((newline = buffer.indexOf("\n")) >= 0) {
				const line = buffer.slice(0, newline);
				buffer = buffer.slice(newline + 1);
				if (!line.trim()) continue;
				const records = JSON.parse(line) as Job[];
				const jobs = records.map((j) => Object.assign(new Job(), j));
				parseDates<Job>(jobs);
				all.push(...jobs);
				onBatch(jobs);
			}
		};

		const inflator = new Inflate({ to: "string" });
		// `to: "string"` makes pako emit string chunks; its types stay Uint8Array | string.
		inflator.onData = (chunk) => handleText(chunk as unknown as string);

		const reader = response.body.getReader();
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			inflator.push(value, false);
			if (inflator.err) throw new Error(`Failed to decompress jobs stream: ${inflator.msg}`);
		}
		inflator.push(new Uint8Array(0), true);
		if (inflator.err) throw new Error(`Failed to decompress jobs stream: ${inflator.msg}`);
		return all;
	}

	public async attachmentsByIds(ids: string[]): Promise<Record<string, Job["attachments"]>> {
		if (ids.length === 0) return {};
		const url = `${getBaseDataURL("jobs")}/get-attachments`;
		const response = await apiClient.post<Record<string, Job["attachments"]>>(
			url,
			{ job_ids: ids },
			StandardAxiosConfig(),
		);
		if (response.status !== 200) {
			throw new Error(`Failed to get job attachments`);
		}
		return response.data;
	}

	public async lastCropOptions(): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/last-crop`;
		const response = await apiClient.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get last crop options`);
		} else {
			return response.data;
		}
	}

	public async nextCropOptions(): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/next-crop`;
		const response = await apiClient.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get next crop options`);
		} else {
			return response.data;
		}
	}
}

class UpdateAPI_Jobs extends UpdateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}

	/**
	 * Update a single field on a single Job. `field` is a `keyof Job` (e.g.
	 * `"dealIds"`, `"branchIds"`); the BE translates this to the underlying
	 * Airtable column. `value` must be in the shape Airtable expects: `["recXXX"]`
	 * for a linked-record field, a string for text, etc.
	 *
	 * Use this instead of `many([fullJob])` when you've loaded a Job via a lite
	 * payload (matcher pages, etc.) — `many` round-trips every field, which would
	 * clear any column not present in the lite payload.
	 */
	async field<K extends keyof Job>(id: string, field: K, value: unknown): Promise<Job> {
		const url = `${getBaseDataURL("jobs")}/update/${id}/${field}`;
		const response = await apiClient.post<Job>(url, value, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to update '${String(field)}' on job ${id}: ${response.statusText}`);
		}
		const updated = Object.assign(new Job(), response.data);
		parseDates<Job>([updated]);
		return updated;
	}

	async files(id: string, fileList: File[]) {
		const url = `${getBaseDataURL("jobs")}/upload-file/${id}`;

		async function upload(file: File) {
			const formData = new FormData();
			formData.append("file", file);

			const response = await apiClient.post(url, formData, StandardAxiosConfig(10, { isFileUpload: true }));
			if (response.status !== 200) {
				throw new Error(`Failed to upload file: ${file.name}`);
			} else {
				return response.data;
			}
		}

		if (!fileList) return [];
		const fileArray = Array.from(fileList);
		const uploadResults = await Promise.all(
			fileArray.map(async (file) => {
				const s3url = await upload(file);
				return { url: s3url, filename: file.name } as IAirtableAttachment;
			}),
		);

		return uploadResults;
	}
}

class JobIssuesAPI extends DataAPI_Base<Job> {
	constructor() {
		super("jobs", Job);
	}

	async getAgrian(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/agrian`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get Agrian issues`);
		} else {
			const jobs = response.data.map((j) => Object.assign(new Job(), j));
			parseDates<Job>(jobs);
			return jobs;
		}
	}

	async fixAgrian(ids: string[]): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/agrian`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix Agrian issues`);
		} else {
			return response.data;
		}
	}

	async getDataSendback(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/data-sendback`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get data sendback issues`);
		} else {
			const jobs = response.data.map((j) => Object.assign(new Job(), j));
			parseDates<Job>(jobs);
			return jobs;
		}
	}

	async getDealMatcherJobs(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/deal-matcher`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get unmatched deal jobs`);
		} else {
			const jobs = response.data.map((j) => Object.assign(new Job(), j));
			parseDates<Job>(jobs);
			return jobs;
		}
	}

	async getBranchMatcherJobs(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/branch-matcher`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get unmatched branch jobs`);
		} else {
			const jobs = response.data.map((j) => Object.assign(new Job(), j));
			parseDates<Job>(jobs);
			return jobs;
		}
	}

	async fixDealMatcherJobs(ids: string[]): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/deal-matcher`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix unmatched deal jobs`);
		} else {
			return response.data;
		}
	}

	async getExeMissionValidationJobs(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/exe-mission-validation`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get Exe Mission Validation jobs`);
		} else {
			const jobs = response.data.map((j) => Object.assign(new Job(), j));
			parseDates<Job>(jobs);
			return jobs;
		}
	}

	async fixExeMissionValidationJobs(ids: string[], force = false): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/exe-mission-validation${force ? "?force=true" : ""}`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix Exe Mission Validation jobs`);
		} else {
			return response.data;
		}
	}

	async fixDataSendback(ids: string[], forceEmail = false, forceApi = false): Promise<void> {
		const params = new URLSearchParams();
		if (forceEmail) params.set("force_email", "true");
		if (forceApi) params.set("force_api", "true");
		const qs = params.toString();
		const url = `${getBaseDataURL("jobs")}/issues/data-sendback${qs ? `?${qs}` : ""}`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix data sendback issues`);
		}
		return response.data;
	}
}
export class JobsAPI extends DataAPI_Base<Job> {
	get: GetAPI_Jobs;
	create: CreateAPI_Jobs;
	update: UpdateAPI_Jobs;
	issues: JobIssuesAPI;

	// job data is compressed, as it can get quite large
	constructor() {
		super("jobs", Job, true);
		this.get = new GetAPI_Jobs();
		this.create = new CreateAPI_Jobs();
		this.update = new UpdateAPI_Jobs();
		this.issues = new JobIssuesAPI();
	}
}
